import S, { DataSignal } from 's-js';
import SArray, { SArray as ArraySignal } from 's-array'

/**
 * Configuration options for a source array passed to `zipMap`.
 */
export interface ZipSourceOptions<T> {
    /** The source array. */
    arr : () => readonly T[];
    /**
     * Whether to align the left edges or the right edges of the source and target arrays when zipping. Defaults
     * to `'left'`.
     */
    align? : 'left' | 'right';
    /**
     * A function to pass as the `eq` param to `S.value` when constructing zip accessor signals for this source.
     * Defaults to `undefined` (triggers default `S.value` behavior).
     */
    eq? : (a : T | undefined, b : T | undefined) => boolean;
}

/**
 * A source array passed to `zipMap`. Either a plain array signal (to request all default options) or an options
 * object.
 */
export type ZipSource<T> = (() => readonly T[]) | ZipSourceOptions<T>;

export type ZipSources<TItems extends any[]> = { [K in keyof TItems] : ZipSource<TItems[K]> };

export type ZipAccessor<T> = () => T | undefined;

export type ZipAccessors<TItems extends any[]> = { [K in keyof TItems] : ZipAccessor<TItems[K]> };

export type ZipMapFn<T, TSourceItems extends any[], U> =
    (...args : [v : T, ...zipAccessors : ZipAccessors<TSourceItems>, m : U | undefined, i : number]) => U;

// Internal types used below.
type ZipSignalMap<T> = Map<number, DataSignal<T | undefined>>;

type ZipAccessorMap<T> = Map<number, ZipAccessor<T>>;

type ZipSignalMaps<TItems extends any[]> = { [K in keyof TItems] : ZipSignalMap<TItems[K]> };

type ZipAccessorMaps<TItems extends any[]> = { [K in keyof TItems] : ZipAccessorMap<TItems[K]> };

/**
 * Zip one or more source arrays onto a target `SArray` and map to a results array via the provided mapping
 * function. The mapping function is called with an item of the target array followed by one "zip accessor"
 * signal for each source array. When evaluated, the zip accessor signal returns the item from that source
 * array that's parallel with the given item from the target array, or undefined if there's no such item.
 * @param target The target array to be mapped.
 * @param sources A variable number of source arrays that will be "zipped onto" the target array.
 * @param fn The mapping function to apply to items of the target array. Will also be passed "zip accessor"
 * signals that allow access to the corresponding items from the source arrays.
 */
export function zipMap<T, TSourceItems extends any[], U>(...args : [
    target : ArraySignal<T>,
    ...sources : ZipSources<TSourceItems>,
    fn : ZipMapFn<T, TSourceItems, U>
]) {
    // Destructure args by hand as rest destructuring in this case gives, "A rest element must be last in a
    // destructuring pattern." (Our ...sources bit is in the middle instead of at the end).
    const
        target = args[0],
        fn = args[args.length - 1] as ZipMapFn<T, TSourceItems, U>,
        sources = args.slice(1, args.length - 1) as ZipSources<TSourceItems>;

    let nextKey = 0;

    const
        itemsByKey = new Map<number, T>(),
        zipSignalMaps = sources.map(_ => new Map<any, any>()) as ZipSignalMaps<TSourceItems>,
        zipAccessorMaps = sources.map(_ => new Map<any, any>()) as ZipAccessorMaps<TSourceItems>,
        // We'll assign items in the target array a unique key that we use to do lookups in the zip signal maps.
        // Note that if the target array has duplicate items, we assign each INSTANCE of a given item its own key.
        keys = target.map(
            item => {
                // Item is entering. Assign it a unique key and make new zip signals for it.
                const k = nextKey++;
                itemsByKey.set(k, item);
                for (let j = 0; j < sources.length; j++) {
                    // We init all new zip signals as empty (undefined) and then set the correct value when we
                    // apply zipping below.
                    const signal = S.value(undefined, getEq(sources[j]));
                    zipSignalMaps[j].set(k, signal);
                    // Also stash a readonly version to expose to calling code.
                    zipAccessorMaps[j].set(k, () => signal());
                }
                return k;
            },
            (_, k) => {
                // Item is exiting. Remove from our maps (and thus free zip signals for GC).
                itemsByKey.delete(k);
                for (let j = 0; j < zipSignalMaps.length; j++) {
                    zipSignalMaps[j].delete(k);
                    zipAccessorMaps[j].delete(k);
                }
            }
        );

    // Here's where we actually zip everything together. We do this in one big S computation for timing reasons:
    // We want it to be the case that if the target array and/or one or more source arrays are all updated in
    // the same tick of S's clock, then any updates to our zip signals all land together in some subsequent tick
    // of S's clock.
    // TODO: Maybe tune performance? The tradeoff w/ doing this as one big S computation is that it means we
    // re-apply all zipping anytime any of our target or source arrays changes. Runtime there is O(NM) where N
    // = number of items in target array and M = number of source arrays. The alternative is we try to track
    // exactly which array(s) are changing, maybe in combo with some array diffing, and do more focused updates.
    // But not gonna try too hard for now. SArray's map method is at least O(N) when the input array changes
    // (actually more like O(N^2) in common cases I think), so there's no point in trying to do better than O(N)
    // when the target array changes. And my guess is that most use cases for this util will see the number of
    // source arrays be small and will see roughly the same number of items in the target and source arrays, so
    // likely little benefit in diffing the source array vs just running back through the target array.
    S(() => {
        const _keys = keys();
        for (let j = 0; j < sources.length; j++) {
            applyZipping(_keys, sources[j], zipSignalMaps[j]);
        }
    });

    // Ugly: Timing-wise, we wanna make sure that if any new target item is discovered, we set its corresponding
    // zip signals BEFORE we call the provided mapping function for that item. That's to avoid calling the
    // mapping function twice, once w/ all zip signals empty (evaluating to undefined) and again w/ the zip
    // signals populated correctly. To make that work, we need to introduce an extra tick of S's clock btwn when
    // our keys array updates and when we run the mapping function. This way, updates end up happening in 3
    // phases:
    // 1) Update zip signals that have been passed into existing mapped items.
    // 2) Map any new target items, with zip signals already populated correctly.
    // 3) Notify consumers of our mapped output array of the change to that array.
    const keys2 = SArray<number>([]);
    S(() => keys2(keys()));

    // Finally, apply the provided mapping function.
    return keys2.map<U>((k, m, i) => {
        const zipAccessors = zipAccessorMaps.map(map => map.get(k)) as ZipAccessors<TSourceItems>;
        return fn(itemsByKey.get(k)!, ...zipAccessors, m, i);
    });
}

function applyZipping<T>(keys : number[], source : ZipSource<T>, signalMap : ZipSignalMap<T>) {
    const
        arr = getArr(source)(),
        align = getAlign(source);
    for (let i = 0; i < keys.length; i++) {
        signalMap.get(keys[i])!(selectMatch(i, keys.length, arr, align));
    }
}

/**
 * Selecting matching element from the source array to zip onto a given element of the target array.
 * @param i Array index of the element in the target array.
 * @param targetLength Length of the target array.
 * @param sourceArr The source array.
 * @param align Whether to align left edges or right edges when zipping.
 */
function selectMatch<T>(i : number, targetLength : number, sourceArr : readonly T[], align : 'left' | 'right') {
    return (
        align === 'right' ? sourceArr[i + sourceArr.length - targetLength] : sourceArr[i]
    ) as T | undefined;
}

function getArr<T>(source : ZipSource<T>) {
    return typeof source === 'function' ? source : source.arr;
}

function getAlign(source : ZipSource<any>) {
    // Default to aligning left edges
    return (typeof source === 'function' ? undefined : source.align) ?? 'left';
}

function getEq<T>(source : ZipSource<T>) {
    return typeof source === 'function' ? undefined : source.eq;
}



// WEBPACK FOOTER //
// ./lib/zipMap.ts